import { TransformedXAxisType } from '@api';
import { useWindowResize } from '@helpers';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useRef, useState } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  SingleSeries,
  Y_AXIS_LABEL_PERCENTAGE,
} from '../apache-config';
import { RemoteLegend } from '../RemoteLegend';
import { tooltipFormatter } from '../utils';

const seriesOptions = {
  endLabel: {
    show: true,
    formatter: '{a}',
    distance: 20,
  },
  lineStyle: {
    width: 4,
  },
  smooth: true,
  symbolSize: 20,
};

export interface BumpChartProps extends ApacheChartBase {
  /** Discriminator. Always `'apache-bump'`. */
  chartType: 'apache-bump';
  /** Categories rendered on the x-axis (typically time periods). */
  xAxis: TransformedXAxisType;
  /** Series to render — each tracks one entity's rank/value across `xAxis` periods. */
  series: SingleSeries[];
}

/**
 * Bump chart: smoothed ranking lines with end-labels. Used for
 * "rank over time" comparisons (e.g. a watchlist's positions in a
 * leaderboard across periods).
 *
 * Wraps `ApacheChart` with smoothed line settings, end-label markers,
 * and the shared `RemoteLegend`.
 */
export const BumpChart = ({
  theme = 'dark_default',
  chartType,
  xAxis,
  series,
  onInit,
  isModal,
}: BumpChartProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const { width, height } = useWindowResize({ targetRef: chartRef });
  const [legendInstance, setLegendInstance] = useState<Types.ECharts | null>(
    null
  );

  useEffect(() => {
    if (chartRef.current) {
      const chartDom = getInstanceByDom(chartRef.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(chartRef.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const seriesData: echarts.SeriesOption[] = series.map(i => ({
      ...seriesOptions,
      ...i,
      type: 'line' as const,
    }));

    const chartWidth = width || DEFAULT_CHART_WIDTH;
    const yAxisLabelWidth = chartWidth * Y_AXIS_LABEL_PERCENTAGE;

    const option = createApacheConfig({
      theme,
      isModal,
      yAxisLabelWidth,
      options: {
        animationDuration: 0,
        tooltip: {
          trigger: 'item',
          formatter: params => {
            return tooltipFormatter({
              isShowTitle: true,
              isShowMarker: true,
              params: params as Types.DefaultLabelFormatterCallbackParams,
            });
          },
        },
        xAxis: {
          type: 'category',
          data: xAxis,
          boundaryGap: false,
          splitLine: {
            show: true,
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            margin: 20,
            formatter: '{value}',
          },
        },
        grid: {
          bottom: '5%',
        },
        legend: { show: false },
        series: seriesData,
      },
    });

    chartDom.setOption(option);

    setLegendInstance(chartDom);

    if (wrapperRef.current) {
      onInit?.(wrapperRef.current);
    }

    return () => {
      chartDom.dispose();
      setLegendInstance(null);
    };
  }, [chartType, theme, xAxis, series, width, height, isModal]);

  return (
    <div ref={wrapperRef}>
      <div ref={chartRef} />
      {legendInstance && (
        <RemoteLegend
          theme={theme}
          legendData={series.map(s => ({ name: s.name, type: 'line' }))}
          targetChart={legendInstance}
        />
      )}
    </div>
  );
};
